import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Bell, BellOff, BellRing, Loader2, Info } from "lucide-react";
import { toast } from "sonner";

function urlBase64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

function isIos() {
  if (typeof navigator === "undefined") return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent || "");
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export default function EnableNotificationsBanner() {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "denied"
  );
  const [subscribed, setSubscribed] = useState(false);
  const [endpoint, setEndpoint] = useState(null);
  const [busy, setBusy] = useState(false);
  const ios = isIos();
  const standalone = isStandalone();

  const refresh = useCallback(async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      setSupported(false);
      return;
    }
    setSupported(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration("/");
      if (!reg) {
        setSubscribed(false);
        return;
      }
      const sub = await reg.pushManager.getSubscription();
      setSubscribed(Boolean(sub));
      setEndpoint(sub?.endpoint || null);
    } catch (e) {
      setSubscribed(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const enable = async () => {
    setBusy(true);
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
        toast.error("Notifications are not supported in this browser.");
        return;
      }
      const reg =
        (await navigator.serviceWorker.getRegistration("/")) ||
        (await navigator.serviceWorker.register("/sw.js", { scope: "/" }));
      await navigator.serviceWorker.ready;

      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        toast.error("Notification permission was not granted.");
        return;
      }

      const { data } = await api.get("/admin/push/vapid-public-key");
      const existing = await reg.pushManager.getSubscription();
      const sub =
        existing ||
        (await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(data.public_key),
        }));

      await api.post("/admin/push/subscribe", sub.toJSON());
      setSubscribed(true);
      setEndpoint(sub.endpoint);
      toast.success("Notifications enabled");
    } catch (e) {
      toast.error("Failed to enable notifications");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration("/");
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        try {
          await api.post("/admin/push/unsubscribe", { endpoint: sub.endpoint });
        } catch (e) {
          /* ignore */
        }
        await sub.unsubscribe();
      }
      setSubscribed(false);
      setEndpoint(null);
      toast.success("Notifications disabled");
    } catch (e) {
      toast.error("Failed to disable notifications");
    } finally {
      setBusy(false);
    }
  };

  // iOS not installed as PWA → show install hint
  if (ios && !standalone) {
    return (
      <div
        className="mx-6 lg:mx-8 mt-4 mb-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2"
        data-testid="ios-pwa-hint"
      >
        <Info className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <strong>iPhone users:</strong> to receive AGUP2 notifications, open this site in Safari,
          tap <em>Share</em> → <em>Add to Home Screen</em>, then open the installed app and grant
          notification permission.
        </div>
      </div>
    );
  }

  if (!supported) return null;

  if (permission === "denied") {
    return (
      <div
        className="mx-6 lg:mx-8 mt-4 mb-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 flex items-center gap-2"
        data-testid="notif-blocked-banner"
      >
        <BellOff className="h-4 w-4 shrink-0" />
        Notifications are blocked. Enable them in your browser site settings to receive new-ticket alerts.
      </div>
    );
  }

  return (
    <div
      className="mx-6 lg:mx-8 mt-4 mb-2 rounded-lg border border-[color:var(--enka-blue-100)] bg-[color:var(--enka-blue-50)] px-4 py-3 text-sm text-[color:var(--enka-ink)] flex items-center gap-3"
      data-testid="notif-banner"
    >
      {subscribed ? (
        <BellRing className="h-4 w-4 text-[color:var(--enka-blue)]" />
      ) : (
        <Bell className="h-4 w-4 text-[color:var(--enka-blue)]" />
      )}
      <div className="flex-1">
        {subscribed
          ? "Push notifications are ON for new AGUP2 tickets on this device."
          : "Get a notification on this device whenever a new ticket is submitted."}
      </div>
      {subscribed ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={disable}
          disabled={busy}
          data-testid="notif-disable-btn"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Disable"}
        </Button>
      ) : (
        <Button
          type="button"
          size="sm"
          onClick={enable}
          disabled={busy}
          className="bg-[color:var(--enka-blue)] hover:bg-[color:var(--enka-blue-600)] text-white"
          data-testid="notif-enable-btn"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enable Notifications"}
        </Button>
      )}
    </div>
  );
}
